<script setup lang="ts">
import AtaTable from '@/components/ata/AtaTable.vue';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { ataService } from '@/services/ata/ata-service';
import { entraAuthService } from '@/services/auth/entra-auth-service.ts';
import { notificationService } from '@/services/notification/notification-service';
import { PhFileArrowDown } from '@phosphor-icons/vue';
import { models, pagePermissions, utils } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();

const route = useRoute();

const currentUser: models.GetMeResponse | null = entraAuthService.me.value;

const createNewAta = async () => {
  await router.push({
    name: RouteName.ATADetails,
    params: { id: route.params.id, ataId: 0 },
  });
};

const exportToCSV = async () => {
  overlaySpinnerService.show();
  try {
    const file = await ataService.exportToCsv(
      utils.type.asType<string>(route.params.id),
    );
    if (file) {
      const blob = new Blob([file], { type: 'text/csv' });
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `ata-${currentDate}.csv`;
      utils.downloadFile(blob, fileName);
    } else {
      notificationService.open({
        text: t('notifications.exportToCsvError'),
        type: 'danger',
      });
    }
  } finally {
    overlaySpinnerService.hide();
  }
};

const permissionTo = computed(() => {
  const projectId = route.params.id.toString();
  return pagePermissions(projectId, currentUser)?.ataFs;
});
</script>
<template>
  <h1 class="is-size-4 mb-4">{{ t('ata.title') }}</h1>
  <AtaTable
    :has-permission-to="{
      edit: {
        ata: permissionTo?.edit.invoiced,
      },
    }"
  ></AtaTable>
  <button
    v-if="permissionTo?.create.ata"
    class="button"
    @click="createNewAta()"
    data-test="create-ata"
  >
    {{ t('ata.createNewAtaButton') }}
  </button>
  <button
    class="button is-text ml-4"
    @click="exportToCSV()"
    data-test="export-to-csv"
  >
    <PhFileArrowDown class="mr-1" :size="22" />
    {{ t('exportToCsvButton') }}
  </button>
</template>
